import { useLocation } from 'react-router-dom';
import { useEffect } from 'react';

interface BreadcrumbItem {
  name: string;
  item: string;
}

// Explicit route overrides (highest priority)
const routeToBreadcrumb: Record<string, BreadcrumbItem[]> = {
  '/': [
    { name: 'Home', item: 'https://yachtvalues.com' }
  ],
  '/valuation': [
    { name: 'Home', item: 'https://yachtvalues.com' },
    { name: 'Yacht Valuation', item: 'https://yachtvalues.com/valuation' }
  ],
  '/resources/faqs': [
    { name: 'Home', item: 'https://yachtvalues.com' },
    { name: 'Resources', item: 'https://yachtvalues.com/resources' },
    { name: 'FAQs', item: 'https://yachtvalues.com/resources/faqs' }
  ],
  '/blog': [
    { name: 'Home', item: 'https://yachtvalues.com' },
    { name: 'Insights', item: 'https://yachtvalues.com/blog' }
  ],
  '/about': [
    { name: 'Home', item: 'https://yachtvalues.com' },
    { name: 'About', item: 'https://yachtvalues.com/about' }
  ],
  '/contact': [
    { name: 'Home', item: 'https://yachtvalues.com' },
    { name: 'Contact', item: 'https://yachtvalues.com/contact' }
  ],
  '/ai-boat-valuation': [
    { name: 'Home', item: 'https://yachtvalues.com' },
    { name: 'AI Yacht Valuation', item: 'https://yachtvalues.com/ai-boat-valuation' }
  ],
  '/finance': [
    { name: 'Home', item: 'https://yachtvalues.com' },
    { name: 'Yacht Financing', item: 'https://yachtvalues.com/finance' }
  ],
  '/market-report': [
    { name: 'Home', item: 'https://yachtvalues.com' },
    { name: 'Market Intelligence', item: 'https://yachtvalues.com/market-report' }
  ],
  '/privacy': [
    { name: 'Home', item: 'https://yachtvalues.com' },
    { name: 'Privacy Policy', item: 'https://yachtvalues.com/privacy' }
  ],
  '/terms': [
    { name: 'Home', item: 'https://yachtvalues.com' },
    { name: 'Terms of Service', item: 'https://yachtvalues.com/terms' }
  ],
};

// Parent segment display names for dynamic breadcrumb generation
const segmentNames: Record<string, string> = {
  'boat-value': 'Yacht Values',
  'guides': 'Guides',
  'intelligence': 'Intelligence Center',
  'tools': 'Tools',
  'resources': 'Resources',
  'blog': 'Insights',
  'admin': 'Admin',
  'dashboard': 'Dashboard',
};

// Convert a URL slug to a display name
function slugToName(slug: string): string {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Dynamically generate breadcrumbs from path segments
function generateBreadcrumbs(pathname: string): BreadcrumbItem[] | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return null;

  const breadcrumbs: BreadcrumbItem[] = [
    { name: 'Home', item: 'https://yachtvalues.com' }
  ];

  let currentPath = '';
  for (let i = 0; i < segments.length; i++) {
    currentPath += '/' + segments[i];
    const name = segmentNames[segments[i]] || slugToName(segments[i]);
    breadcrumbs.push({
      name,
      item: `https://yachtvalues.com${currentPath}`
    });
  }

  return breadcrumbs.length > 1 ? breadcrumbs : null;
}

// Routes that should NOT get breadcrumbs (auth-walled, admin, etc.)
const noIndexPrefixes = ['/admin', '/dashboard', '/auth', '/account', '/settings'];

export default function BreadcrumbSchema() {
  const location = useLocation();

  useEffect(() => {
    // Skip breadcrumbs for protected/admin routes
    if (noIndexPrefixes.some(prefix => location.pathname.startsWith(prefix))) {
      const existingScript = document.querySelector('script[data-breadcrumb="true"]');
      if (existingScript) existingScript.remove();
      return;
    }

    // Use explicit mapping first, then dynamic generation
    const breadcrumbs = routeToBreadcrumb[location.pathname] || generateBreadcrumbs(location.pathname);
    
    if (breadcrumbs && breadcrumbs.length > 1) {
      // Remove existing breadcrumb schema
      const existingScript = document.querySelector('script[data-breadcrumb="true"]');
      if (existingScript) {
        existingScript.remove();
      }

      // Add new breadcrumb schema
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-breadcrumb', 'true');
      script.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((crumb, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": crumb.name,
          "item": crumb.item
        }))
      });
      document.head.appendChild(script);
    }

    return () => {
      const existingScript = document.querySelector('script[data-breadcrumb="true"]');
      if (existingScript) existingScript.remove();
    };
  }, [location.pathname]);

  return null;
}
